import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertCircle,
  Briefcase,
  CheckCircle2,
  ChevronRight,
  FileText,
  Sparkles,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useFeaturePreviewCopy } from './useFeaturePreviewCopy';
import { useHeroPreviewCopy } from './useHeroPreviewCopy';
import { useAtsPreviewJobs } from './usePreviewJobFixtures';

const TYPING_MS = 28;

type SimPhase =
  | 'typing-query'
  | 'searching'
  | 'jobs'
  | 'cursor-job1'
  | 'bad-match'
  | 'cursor-job2'
  | 'rewrite-ask'
  | 'typing-yes'
  | 'canvas-loading'
  | 'canvas-done';

type PreviewJob = {
  id: string;
  title: string;
  company: string;
  location: string;
  score: number;
  source: string;
  skills: string[];
  jobType: string;
  experienceLevel: string;
  salary: string;
  postedLabel: string;
};

const RESUME_SECTIONS = [
  {
    title: 'Professional Summary',
    lines: [
      'B2B SaaS product leader with 6+ years driving roadmap strategy,',
      'cross-functional delivery, and measurable enterprise adoption.',
    ],
  },
  {
    title: 'Experience',
    lines: [
      'Senior Product Manager · SaaS Co · 2021–Present',
      '• Drove 3× revenue growth leading a 12-person roadmap team',
      '• Shipped 6 enterprise features to 40K+ users, +28% retention',
    ],
  },
  {
    title: 'Skills',
    lines: ['Roadmaps · B2B SaaS · Analytics · SQL · Azure · Stakeholder Mgmt'],
  },
];

const BEFORE_SCORE = 42;
const AFTER_SCORE = 87;

function PreviewMatchRing({
  score,
  ringId,
  highlight = false,
  size = 'sm',
}: {
  score: number;
  ringId: string;
  highlight?: boolean;
  size?: 'sm' | 'md';
}) {
  const dim = size === 'md' ? 44 : 36;
  const radius = size === 'md' ? 17 : 14;
  const center = dim / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (Math.max(0, Math.min(100, score)) / 100) * circumference;
  const gradientId = `ats-preview-ring-${ringId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const scoreColor = score >= 80 ? '#10b981' : score >= 65 ? '#6366f1' : '#f59e0b';

  return (
    <div
      className={`home-hero-preview-match-ring relative shrink-0 ${size === 'md' ? 'h-11 w-11' : 'h-9 w-9'} ${highlight ? 'hfs-agent-preview__ring--highlight' : ''}`}
      aria-label={`${score} percent match`}
    >
      <svg width={dim} height={dim} viewBox={`0 0 ${dim} ${dim}`} className="block" aria-hidden>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="50%" stopColor="#6C47FF" />
            <stop offset="100%" stopColor="#7c3aed" />
          </linearGradient>
        </defs>
        <circle cx={center} cy={center} r={radius} fill="none" stroke="rgba(148, 163, 184, 0.22)" strokeWidth="2.5" />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={score >= 65 ? `url(#${gradientId})` : scoreColor}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className={`font-bold uppercase tracking-wide text-slate-400 ${size === 'md' ? 'text-[5px]' : 'text-[4px]'}`}>
          Match
        </span>
        <span className={`font-black text-gray-950 dark:text-white ${size === 'md' ? 'text-[9px]' : 'text-[8px]'}`}>
          {score}%
        </span>
      </div>
    </div>
  );
}

function AtsScoreRing({ score, size = 48 }: { score: number; size?: number }) {
  const stroke = 4;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - score / 100);
  const color = score >= 70 ? '#16a34a' : score >= 45 ? '#d97706' : '#dc2626';

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgba(0,0,0,0.08)" strokeWidth={stroke} />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: dashOffset }}
          transition={{ duration: 1.1, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="text-[13px] font-bold" style={{ color }}>{score}</span>
        <span className="text-[7px] font-semibold uppercase tracking-wide" style={{ color }}>ATS</span>
      </div>
    </div>
  );
}

function PreviewJobCard({
  job,
  selected,
  dimmed,
  rejected,
  highlightRing,
  cardRef,
}: {
  job: PreviewJob;
  selected?: boolean;
  dimmed?: boolean;
  rejected?: boolean;
  highlightRing?: boolean;
  cardRef?: React.Ref<HTMLButtonElement>;
}) {
  const p = useHeroPreviewCopy();
  const initial = job.company.charAt(0).toUpperCase();
  const visibleSkills = job.skills.slice(0, 3);
  const extraSkills = Math.max(0, job.skills.length - 3);

  return (
    <button
      ref={cardRef}
      type="button"
      className={`home-hero-preview-job-card home-hero-preview-job-card--portrait home-hero-preview-job-card--with-ring flex h-full w-full min-h-0 flex-col text-left ${
        selected ? 'home-hero-preview-job-card--selected' : ''
      } ${rejected ? 'opacity-50' : ''}`}
      style={{ opacity: dimmed ? 0.45 : rejected ? 0.55 : undefined }}
    >
      <div className="absolute right-1 top-1 z-[1]">
        <PreviewMatchRing score={job.score} ringId={job.id} highlight={highlightRing} />
      </div>
      <div className="relative shrink-0">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-[11px] font-bold text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300">
          {initial}
        </div>
      </div>
      <div className="mt-1.5 min-w-0 shrink-0 pr-9">
        <p className="line-clamp-2 text-[11px] font-bold leading-snug text-gray-950 dark:text-white">{job.title}</p>
        <p className="mt-0.5 line-clamp-1 text-[10px] font-medium text-slate-600 dark:text-gray-400">{job.company}</p>
        <p className="mt-0.5 text-[9px] leading-snug text-slate-400">
          {job.source} · {job.postedLabel}
        </p>
      </div>
      <div className="home-hero-preview-job-card__tags mt-1.5 flex min-w-0 flex-col gap-0.5">
        {visibleSkills.map((skill) => (
          <span key={skill} className="home-hero-preview-job-card__tag w-full truncate">
            {skill}
          </span>
        ))}
        {extraSkills > 0 ? (
          <span className="home-hero-preview-job-card__tag home-hero-preview-job-card__tag--more w-fit">
            {p.jobsExtraSkills.replace('{{count}}', String(extraSkills))}
          </span>
        ) : null}
      </div>
      {rejected && (
        <span className="mt-1 inline-flex w-fit items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[7px] font-semibold text-amber-700 dark:bg-amber-950/30 dark:text-amber-300">
          <AlertCircle className="h-2 w-2" />
          {p.jobsLowMatch}
        </span>
      )}
      <p className="mt-1 shrink-0 text-[9px] leading-snug text-slate-400">{job.location}</p>
      <div className="home-hero-preview-job-card__footer mt-auto shrink-0">
        <p className="truncate text-[9px] text-slate-400">
          {job.experienceLevel} · {job.salary}
        </p>
        <span className="home-hero-preview-job-card__arrow" aria-hidden>
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.25} />
        </span>
      </div>
    </button>
  );
}

function PreviewCursor({
  x,
  y,
  clicking,
  visible,
}: {
  x: number;
  y: number;
  clicking: boolean;
  visible: boolean;
}) {
  if (!visible) return null;

  return (
    <motion.div
      className="home-hero-preview-cursor pointer-events-none absolute z-[80]"
      initial={false}
      animate={{ left: x, top: y, scale: clicking ? 0.84 : 1, opacity: 1 }}
      transition={{ type: 'spring', stiffness: 280, damping: 24, mass: 0.55 }}
    >
      <svg width="16" height="20" viewBox="0 0 18 22" fill="none" aria-hidden className="drop-shadow-sm">
        <path d="M1 1L1 16L5 12L8 20L11 19L8 11L14 10L1 1Z" fill="#0f172a" stroke="#ffffff" strokeWidth="1.25" />
      </svg>
      {clicking ? <span className="home-hero-preview-cursor-ripple" aria-hidden /> : null}
    </motion.div>
  );
}

function AgentBubble({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'neutral' | 'warn' | 'success' }) {
  const bg =
    tone === 'warn'
      ? 'border-amber-100/70 bg-gradient-to-br from-amber-50/70 to-orange-50/40 dark:border-amber-900/40 dark:from-amber-950/20'
      : tone === 'success'
        ? 'border-emerald-100/70 bg-gradient-to-br from-emerald-50/60 to-indigo-50/30 dark:border-emerald-900/40'
        : 'border-indigo-100/60 bg-gradient-to-br from-indigo-50/50 to-sky-50/30 dark:border-indigo-900/40';

  return (
    <div className={`rounded-xl border p-2.5 ${bg}`}>
      <p className="text-[9px] leading-relaxed text-gray-700 dark:text-gray-300">{children}</p>
    </div>
  );
}

function OptimizeCanvasPreview({
  atsScore,
  visibleSections,
  canvasPhase,
  goodJobTitle,
  goodJobCompany,
}: {
  atsScore: number;
  visibleSections: number;
  canvasPhase: 'loading' | 'streaming' | 'done';
  goodJobTitle: string;
  goodJobCompany: string;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col bg-[#f8fafc] dark:bg-[#111113]">
      <div className="flex shrink-0 items-center gap-2 border-b border-gray-200 bg-white px-3 py-2 dark:border-gray-800 dark:bg-[#0f0f11]">
        <Sparkles className="h-3 w-3 shrink-0 text-indigo-600" />
        <span className="min-w-0 flex-1 truncate text-[10px] font-semibold text-gray-900 dark:text-white">
          Optimize · {goodJobTitle} — {goodJobCompany}
        </span>
        <button type="button" className="shrink-0 text-gray-400" aria-hidden>
          <X className="h-3 w-3" />
        </button>
      </div>

      <div className="flex shrink-0 items-center gap-1 border-b border-gray-200 bg-white px-3 py-1.5 dark:border-gray-800 dark:bg-[#0f0f11]">
        {(['Baseline', 'Optimizing', 'Ready'] as const).map((label, i) => {
          const activeIdx = canvasPhase === 'loading' ? 0 : canvasPhase === 'streaming' ? 1 : 2;
          const done = i < activeIdx;
          const active = i === activeIdx;
          return (
            <React.Fragment key={label}>
              {i > 0 && (
                <div className={`h-px flex-1 ${done ? 'bg-indigo-400' : 'bg-gray-200 dark:bg-gray-700'}`} />
              )}
              <div className="flex shrink-0 items-center gap-1">
                <div
                  className={`flex h-4 w-4 items-center justify-center rounded-full text-[7px] font-bold ${
                    done
                      ? 'bg-indigo-600 text-white'
                      : active
                        ? 'bg-indigo-100 text-indigo-700 animate-pulse dark:bg-indigo-950/50 dark:text-indigo-300'
                        : 'bg-gray-100 text-gray-400 dark:bg-gray-800'
                  }`}
                >
                  {done ? '✓' : i + 1}
                </div>
                <span className={`text-[8px] font-medium ${active ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-400'}`}>
                  {label}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>

      <div className="relative min-h-0 flex-1 overflow-hidden p-2">
        {canvasPhase === 'loading' ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900/40">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
            <p className="text-[9px] font-medium text-gray-600 dark:text-gray-400">Scoring resume against JD…</p>
          </div>
        ) : (
          <div className="relative mx-auto h-full max-w-full overflow-hidden rounded-sm bg-white shadow-md dark:bg-white">
            <div
              className="pointer-events-none absolute right-2 top-2 z-10"
              data-no-export="true"
            >
              <AtsScoreRing score={atsScore} size={44} />
            </div>

            <div className="h-full overflow-hidden p-3 pt-2">
              <p className="text-[11px] font-bold text-gray-900">Alex Rivera</p>
              <p className="text-[8px] text-gray-500">Senior Product Manager · Remote</p>
              <div className="my-2 h-px bg-gray-200" />

              {RESUME_SECTIONS.slice(0, visibleSections).map((section, si) => (
                <motion.div
                  key={section.title}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: si * 0.05 }}
                  className="mb-2.5"
                >
                  <p className="mb-0.5 border-b border-indigo-200 pb-0.5 text-[7px] font-bold uppercase tracking-wider text-indigo-800">
                    {section.title}
                  </p>
                  {section.lines.map((line) => (
                    <p key={line} className="text-[7.5px] leading-relaxed text-gray-700">
                      {line}
                    </p>
                  ))}
                </motion.div>
              ))}

              {canvasPhase === 'streaming' && visibleSections < RESUME_SECTIONS.length && (
                <span className="inline-block h-3 w-[1.5px] animate-pulse bg-indigo-500 align-middle" />
              )}
            </div>
          </div>
        )}
      </div>

      {canvasPhase === 'done' && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex shrink-0 items-center justify-between border-t border-gray-200 bg-white px-3 py-2 dark:border-gray-800 dark:bg-[#0f0f11]"
        >
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="h-3 w-3 text-emerald-600" />
            <span className="text-[8px] font-semibold text-emerald-700 dark:text-emerald-300">
              +{AFTER_SCORE - BEFORE_SCORE} ATS pts · ready to apply
            </span>
          </div>
          <button type="button" className="hfs-agent-preview__btn hfs-agent-preview__btn--primary px-2 py-1 text-[8px]">
            Apply
          </button>
        </motion.div>
      )}
    </div>
  );
}

const ATSScoreEnginePreview: React.FC = () => {
  const copy = useFeaturePreviewCopy();
  const previewJobs = useAtsPreviewJobs();
  const goodJob = previewJobs[1] ?? previewJobs[0];
  const USER_QUERY = copy.userQueryRecommended;
  const USER_YES = copy.userYesRewrite;
  const rootRef = useRef<HTMLDivElement>(null);
  const job1Ref = useRef<HTMLButtonElement>(null);
  const job2Ref = useRef<HTMLButtonElement>(null);

  const [phase, setPhase] = useState<SimPhase>('typing-query');
  const [typedQueryLen, setTypedQueryLen] = useState(0);
  const [typedYesLen, setTypedYesLen] = useState(0);
  const [searchProgress, setSearchProgress] = useState(0);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [rejectedJobId, setRejectedJobId] = useState<string | null>(null);
  const [cursor, setCursor] = useState({ x: 24, y: 24, visible: false, clicking: false });
  const [canvasPhase, setCanvasPhase] = useState<'loading' | 'streaming' | 'done'>('loading');
  const [visibleSections, setVisibleSections] = useState(0);
  const [atsScore, setAtsScore] = useState(BEFORE_SCORE);

  const showJobsPanel =
    phase !== 'canvas-loading' && phase !== 'canvas-done';
  const showCanvas = phase === 'canvas-loading' || phase === 'canvas-done';
  const showJobs =
    phase === 'jobs' ||
    phase === 'cursor-job1' ||
    phase === 'bad-match' ||
    phase === 'cursor-job2' ||
    phase === 'rewrite-ask' ||
    phase === 'typing-yes';

  const getTargetCenter = useCallback((el: HTMLElement | null) => {
    const root = rootRef.current;
    if (!root || !el) return null;
    const rootRect = root.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    return {
      x: elRect.left - rootRect.left + elRect.width * 0.55,
      y: elRect.top - rootRect.top + elRect.height * 0.5,
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const timers: number[] = [];
    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        timers.push(window.setTimeout(resolve, ms));
      });

    const reset = () => {
      setPhase('typing-query');
      setTypedQueryLen(0);
      setTypedYesLen(0);
      setSearchProgress(0);
      setSelectedJobId(null);
      setRejectedJobId(null);
      setCursor({ x: 24, y: 24, visible: false, clicking: false });
      setCanvasPhase('loading');
      setVisibleSections(0);
      setAtsScore(BEFORE_SCORE);
    };

    const clickJob = async (ref: React.RefObject<HTMLButtonElement | null>, jobId: string) => {
      const start = getTargetCenter(ref.current);
      if (start) {
        setCursor({ ...start, visible: true, clicking: false });
        await sleep(650);
        setCursor((c) => ({ ...c, clicking: true }));
        await sleep(120);
        setSelectedJobId(jobId);
        setCursor((c) => ({ ...c, clicking: false }));
        await sleep(280);
        setCursor((c) => ({ ...c, visible: false }));
      } else {
        setSelectedJobId(jobId);
        await sleep(400);
      }
    };

    const runScript = async () => {
      if (previewJobs.length < 2) return;
      const badJobId = previewJobs[0].id;
      const goodJobId = previewJobs[1].id;
      await sleep(500);
      while (!cancelled) {
        reset();
        await sleep(400);

        setPhase('typing-query');
        for (let i = 0; i <= USER_QUERY.length; i += 1) {
          if (cancelled) return;
          setTypedQueryLen(i);
          await sleep(TYPING_MS);
        }
        await sleep(500);

        setPhase('searching');
        for (let p = 0; p <= 100; p += 8) {
          if (cancelled) return;
          setSearchProgress(Math.min(p, 100));
          await sleep(50);
        }
        await sleep(400);

        setPhase('jobs');
        await sleep(1400);

        setPhase('cursor-job1');
        await clickJob(job1Ref, badJobId);
        setRejectedJobId(badJobId);
        setPhase('bad-match');
        await sleep(2400);

        setPhase('cursor-job2');
        await clickJob(job2Ref, goodJobId);
        setPhase('rewrite-ask');
        await sleep(2200);

        setPhase('typing-yes');
        for (let i = 0; i <= USER_YES.length; i += 1) {
          if (cancelled) return;
          setTypedYesLen(i);
          await sleep(TYPING_MS);
        }
        await sleep(450);

        setPhase('canvas-loading');
        setCanvasPhase('loading');
        await sleep(1200);

        setCanvasPhase('streaming');
        for (let s = 1; s <= RESUME_SECTIONS.length; s++) {
          if (cancelled) return;
          setVisibleSections(s);
          const progress = BEFORE_SCORE + Math.round(((AFTER_SCORE - BEFORE_SCORE) * s) / RESUME_SECTIONS.length);
          setAtsScore(progress);
          await sleep(520);
        }
        await sleep(400);

        setCanvasPhase('done');
        setAtsScore(AFTER_SCORE);
        setPhase('canvas-done');
        await sleep(5200);
      }
    };

    void runScript();
    return () => {
      cancelled = true;
      timers.forEach((id) => window.clearTimeout(id));
    };
  }, [getTargetCenter, USER_QUERY.length, USER_YES.length, previewJobs]);

  const showQueryTyping = phase === 'typing-query' && typedQueryLen < USER_QUERY.length;
  const showYesTyping = phase === 'typing-yes' && typedYesLen < USER_YES.length;
  const queryDone = typedQueryLen >= USER_QUERY.length && phase !== 'typing-query';
  const yesDone = typedYesLen >= USER_YES.length;

  return (
    <div
      ref={rootRef}
      className="relative flex h-full min-h-0 flex-col overflow-hidden bg-white font-sans antialiased dark:bg-[#0f0f11]"
    >
      <PreviewCursor {...cursor} />

      <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-3 py-2 dark:border-gray-800">
        <FileText className="h-3.5 w-3.5 shrink-0 text-indigo-600" />
        <span className="text-[11px] font-semibold text-gray-900 dark:text-white">{copy.atsTitle}</span>
        <span className="ml-auto h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-emerald-500" />
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden md:grid-cols-12">
        {/* Agent panel */}
        <div className="flex min-h-0 flex-col border-b border-gray-200 md:col-span-5 md:border-b-0 md:border-r dark:border-gray-800">
          <div className="flex shrink-0 items-center gap-2 border-b border-gray-100 px-3 py-2 dark:border-gray-800">
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-indigo-600" />
            <span className="text-[11px] font-semibold text-gray-900 dark:text-white">{copy.agentBrand}</span>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden p-3">
            {/* User: recommended jobs */}
            {(phase !== 'typing-query' || typedQueryLen > 0) && (
              <div className="shrink-0">
                <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400">{copy.you}</span>
                <div className="mt-1 min-h-[36px] rounded-xl border border-gray-100 bg-gray-50 px-2.5 py-2 text-[9px] font-medium leading-relaxed text-gray-900 dark:border-gray-800 dark:bg-gray-900/60 dark:text-gray-100">
                  {USER_QUERY.slice(0, typedQueryLen)}
                  {showQueryTyping && (
                    <span className="ml-0.5 inline-block h-3 w-[1.5px] animate-pulse bg-gray-900 align-middle dark:bg-white" />
                  )}
                </div>
              </div>
            )}

            {/* Searching */}
            <AnimatePresence>
              {phase === 'searching' && (
                <motion.div key="search" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="shrink-0">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-indigo-600">{copy.agentRole}</span>
                  <div className="mt-1 rounded-xl border border-indigo-100/60 bg-indigo-50/40 p-2.5 dark:border-indigo-900/40">
                    <p className="text-[9px] font-semibold text-indigo-800 dark:text-indigo-300">
                      {copy.pullingRecommended}
                    </p>
                    <div className="mt-2 h-1 overflow-hidden rounded-full bg-indigo-100/80">
                      <motion.div
                        className="h-full rounded-full bg-indigo-600"
                        animate={{ width: `${searchProgress}%` }}
                        transition={{ duration: 0.04, ease: 'linear' }}
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Jobs found reply */}
            {showJobs && queryDone && phase !== 'searching' && (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="shrink-0">
                <span className="text-[9px] font-bold uppercase tracking-wider text-indigo-600">{copy.agentRole}</span>
                <div className="mt-1">
                  <AgentBubble tone="success">{copy.rolesFoundReply}</AgentBubble>
                </div>
              </motion.div>
            )}

            {/* Bad match */}
            {(phase === 'bad-match' ||
              phase === 'cursor-job2' ||
              phase === 'rewrite-ask' ||
              phase === 'typing-yes' ||
              showCanvas) &&
              rejectedJobId && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="shrink-0">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-indigo-600">{copy.agentRole}</span>
                  <div className="mt-1">
                    <AgentBubble tone="warn">{copy.badMatchReply}</AgentBubble>
                  </div>
                </motion.div>
              )}

            {/* Rewrite ask */}
            {(phase === 'rewrite-ask' || phase === 'typing-yes' || showCanvas) &&
              goodJob &&
              selectedJobId === goodJob.id && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="shrink-0">
                  <span className="text-[9px] font-bold uppercase tracking-wider text-indigo-600">{copy.agentRole}</span>
                  <div className="mt-1">
                    <AgentBubble tone="success">{copy.rewriteAskReply}</AgentBubble>
                  </div>
                </motion.div>
              )}

            {/* User: yes */}
            {(phase === 'typing-yes' || showCanvas) && (typedYesLen > 0 || yesDone) && (
              <div className="shrink-0">
                <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400">{copy.you}</span>
                <div className="mt-1 min-h-[32px] rounded-xl border border-gray-100 bg-gray-50 px-2.5 py-2 text-[9px] font-medium leading-relaxed text-gray-900 dark:border-gray-800 dark:bg-gray-900/60 dark:text-gray-100">
                  {USER_YES.slice(0, typedYesLen)}
                  {showYesTyping && (
                    <span className="ml-0.5 inline-block h-3 w-[1.5px] animate-pulse bg-gray-900 align-middle dark:bg-white" />
                  )}
                </div>
              </div>
            )}

            {/* Canvas opening */}
            {showCanvas && yesDone && (
              <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="shrink-0">
                <span className="text-[9px] font-bold uppercase tracking-wider text-indigo-600">{copy.agentRole}</span>
                <div className="mt-1">
                  <AgentBubble>{copy.openingCanvasReply}</AgentBubble>
                </div>
              </motion.div>
            )}

            <div className="mt-auto shrink-0 rounded-xl border border-dashed border-gray-200 px-2.5 py-1.5 text-[8px] text-gray-400 dark:border-gray-700">
              {copy.promptHintAgent}
            </div>
          </div>
        </div>

        {/* Right panel — jobs or canvas */}
        <div className="relative flex min-h-0 flex-1 flex-col md:col-span-7">
          <AnimatePresence mode="wait">
            {showJobsPanel && (
              <motion.div
                key="jobs-panel"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex h-full min-h-0 flex-col bg-[#fafafa] dark:bg-[#111113]"
              >
                <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-3 py-2 dark:border-gray-800">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                    {copy.recommendedJobs}
                  </span>
                  <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-medium text-emerald-700">
                    {showJobs ? copy.matchesCount(previewJobs.length) : copy.loading}
                  </span>
                </div>

                <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden p-2">
                  <p className="mb-1.5 shrink-0 flex items-center gap-1 text-[9px] font-medium text-slate-500">
                    <Briefcase className="h-2.5 w-2.5" />
                    {showJobs ? copy.rankedSelectRole : copy.waitingRecommendations}
                  </p>

                  <div className="grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-1.5">
                    {previewJobs.map((job, index) => {
                      const isSelected = selectedJobId === job.id;
                      const isRejected = rejectedJobId === job.id;
                      const isTarget1 = index === 0 && (phase === 'cursor-job1' || phase === 'jobs');
                      const isTarget2 = index === 1 && (phase === 'cursor-job2' || phase === 'rewrite-ask');
                      const dimmed =
                        showCanvas ||
                        (selectedJobId !== null && !isSelected && phase !== 'jobs' && phase !== 'searching');

                      return (
                        <motion.div
                          key={job.id}
                          initial={{ opacity: 0.15, y: 6 }}
                          animate={{
                            opacity: showJobs ? (dimmed && !isSelected ? 0.5 : 1) : 0.15,
                            y: 0,
                            scale: isSelected ? 1.02 : 1,
                          }}
                          transition={{ delay: showJobs ? index * 0.07 : 0 }}
                          className="min-h-0"
                        >
                          <PreviewJobCard
                            job={job}
                            selected={isSelected}
                            rejected={isRejected}
                            dimmed={dimmed && !isSelected}
                            highlightRing={showJobs && (isTarget1 || isTarget2 || isSelected)}
                            cardRef={index === 0 ? job1Ref : index === 1 ? job2Ref : undefined}
                          />
                        </motion.div>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}

            {showCanvas && (
              <motion.div
                key="canvas-panel"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.28, ease: 'easeOut' }}
                className="flex h-full min-h-0 flex-col"
              >
                <OptimizeCanvasPreview
                  atsScore={atsScore}
                  visibleSections={visibleSections}
                  canvasPhase={canvasPhase}
                  goodJobTitle={goodJob?.title ?? ''}
                  goodJobCompany={goodJob?.company ?? ''}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default ATSScoreEnginePreview;
